import React, { useMemo, useState } from "react";
import type { TooltipTriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { ClassAvatar } from "@components/avatar/ClassAvatar";
import { ClassAvatarGroup } from "@components/avatar/ClassAvatarGroup";
import {
    Picker,
    pickerTreeRowPlacements,
    type PickerCellEditor,
} from "@design-system/Picker/Picker.lib";
import { useClasses } from "@api/class.api";
import {
    buildClassSearchTokens,
    ClassWithChildren,
    deselectSiblingsInSameRoot,
} from "@helpers/class";
import { ComboboxTriggerButton } from "./ComboboxTriggerButton";
import { getClassesAriaLabel } from "./getClassesAriaLabel";
import styles from "./ClassCombobox.module.scss";

interface Props {
    selectedClassIds?: string[];
    onChange: (classIds: string[]) => void;
    disabled?: boolean;
    placeholder?: string;
    readOnly?: boolean;
    hideArrow?: boolean;
    className?: string;
    triggerButtonProps?: TooltipTriggerButtonProps;
    onOpenChange?: (open: boolean) => void;
    open?: boolean;
    /** A Picker trigger part (e.g. `Picker.CellContent`) replacing the default button. */
    trigger?: React.ReactNode;
    /**
     * When false, multiple classes from the same root group can coexist in
     * the selection.  Defaults to true (original behaviour: selecting a class
     * deselects any sibling in the same root group).
     */
    canSelectOnlyOneClassInTheSameRoot?: boolean;
    /** Spreadsheet grid editors: the opening key seeds the search and the first match is highlighted. */
    cellEditor?: PickerCellEditor;
}

export const ClassCombobox: React.FC<Props> = ({
    selectedClassIds,
    onChange,
    disabled,
    placeholder = "Select class",
    readOnly,
    hideArrow,
    className,
    triggerButtonProps,
    onOpenChange,
    open,
    trigger,
    canSelectOnlyOneClassInTheSameRoot,
    cellEditor,
}) => {
    const { classesWithChildren, classesMap } = useClasses();
    const flatClasses = useMemo(() => {
        const flatten = (nodes: ClassWithChildren[]): ClassWithChildren[] =>
            nodes.flatMap((node) => [node, ...flatten(node.children)]);

        return flatten(classesWithChildren);
    }, [classesWithChildren]);

    const [typedSearch, setTypedSearch] = useState<string>();
    const searchTokensById = useMemo(
        () => buildClassSearchTokens(classesWithChildren),
        [classesWithChildren],
    );

    const filteredClasses = useMemo(() => {
        const normalizedSearch = typedSearch?.trim().toLowerCase();
        if (!normalizedSearch) {
            return flatClasses;
        }
        return flatClasses.filter((cls) =>
            (searchTokensById.get(cls.id) ?? [cls.label.toLowerCase()]).some(
                (token) => token.includes(normalizedSearch),
            ),
        );
    }, [flatClasses, typedSearch, searchTokensById]);

    const rowPlacements = useMemo(
        () => pickerTreeRowPlacements(filteredClasses.map((cls) => cls.depth)),
        [filteredClasses],
    );

    const selectedClasses = useMemo(
        () =>
            (selectedClassIds ?? [])
                .map((id) => classesMap.get(id))
                .filter((cls): cls is ClassWithChildren => Boolean(cls)),
        [selectedClassIds, classesMap],
    );
    const selectedIds = useMemo(
        () => selectedClasses.map((cls) => cls.id),
        [selectedClasses],
    );

    // Apply canSelectOnlyOneClassInTheSameRoot logic:
    // selecting a class deselects any other class in the same root group.
    const handleValueChange = (nextIds: string[]) => {
        const previousSet = new Set(selectedClassIds ?? []);
        let newSet = new Set(nextIds);

        const addedId = nextIds.find((id) => !previousSet.has(id));

        if (addedId && canSelectOnlyOneClassInTheSameRoot !== false) {
            newSet = deselectSiblingsInSameRoot({
                selectedIds: newSet,
                addedClassId: addedId,
                classesMap,
            });
        }

        onChange(Array.from(newSet));
    };

    return (
        <Picker.Select
            multiple
            searchable
            cellEditor={cellEditor}
            value={selectedIds}
            onValueChange={handleValueChange}
            onInputValueChange={(inputValue) =>
                setTypedSearch(inputValue || undefined)
            }
            onOpenChange={onOpenChange}
            disabled={disabled || readOnly}
            open={open}
        >
            {trigger ?? (
                <ComboboxTriggerButton
                    buttonProps={triggerButtonProps}
                    className={className}
                    ariaLabel={getClassesAriaLabel(selectedClasses)}
                    value={
                        selectedClasses.length > 0 ? (
                            <span className={styles.triggerValue}>
                                <ClassAvatarGroup
                                    classes={selectedClasses}
                                    max={3}
                                />
                            </span>
                        ) : undefined
                    }
                    placeholder={placeholder}
                    hideArrow={hideArrow}
                />
            )}
            <Picker.Popup sideOffset={4}>
                <Picker.Search placeholder="Search classes..." />
                <Picker.List>
                    {filteredClasses.map((cls, index) => (
                        <Picker.Item
                            key={cls.id}
                            value={cls.id}
                            depth={cls.depth}
                            treePosition={rowPlacements[index].treePosition}
                            ancestorLines={rowPlacements[index].ancestorLines}
                            leading={
                                cls.parentClassId == null ? (
                                    <ClassAvatar
                                        class={cls}
                                        size="xs"
                                        aria-hidden
                                    />
                                ) : undefined
                            }
                        >
                            {cls.label}
                        </Picker.Item>
                    ))}
                    {filteredClasses.length === 0 && (
                        <Picker.Empty>No matching classes</Picker.Empty>
                    )}
                </Picker.List>
            </Picker.Popup>
        </Picker.Select>
    );
};
